import { useEffect, useState, type ReactNode } from 'react';
import { FilesIcon, FolderOpenIcon, FolderPlusIcon } from 'lucide-react';

import { toast } from 'sonner';

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  useRememberedLayout,
} from '@/components/ui/resizable';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilesMark } from '@/shared/components/emptyMarks';
import { RetryButton } from '@/shared/components/RetryButton';
import { NotFoundState } from '@/shared/components/NotFoundState';
import { PagePlaceholder } from '@/shared/components/PagePlaceholder';
import { Breadcrumb } from './Breadcrumb';
import { FileListPane } from './FileListPane';
import { PreviewPane } from './PreviewPane';
import { FileDragLayerLazy } from './FileDragLayerLazy';
import { MoveToDialog } from './MoveToDialog';
import { NewFolderDialog } from './NewFolderDialog';
import { OverwriteConfirmDialog } from './OverwriteConfirmDialog';
import { RenameDialog } from './RenameDialog';
import { UploadMenu } from './UploadMenu';
import { UploadPickers } from './UploadPickers';
import { UploadTray } from './UploadTray';
import { useCreateWorkspaceFolder } from '../hooks/useCreateWorkspaceFolder';
import { useMoveWorkspaceFile } from '../hooks/useMoveWorkspaceFile';
import { useRenameWorkspaceFile } from '../hooks/useRenameWorkspaceFile';
import { getMoveFailureMessage } from '../errors';
import type { FileEntry, MoveFileRequest } from '@/lib/api/generated/model';
import { useFileUploads, type FileUploads } from '../hooks/useFileUploads';
import type { FileView } from '../searchParams';
import { useFileSortStore } from '../sortStore';
import { useWorkspaceFileListing } from '../hooks/useWorkspaceFileListing';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * Placeholder rows standing in for a folder while it loads. The widths vary
 * because filenames do — a column of identical bars reads as a component, not
 * as a listing arriving.
 */
const SKELETON_ROW_WIDTHS = [
  'w-44',
  'w-28',
  'w-36',
  'w-24',
  'w-40',
  'w-32',
  'w-44',
  'w-28',
];

/**
 * The listing's placeholder, shown while a folder loads and again for the
 * moment the drag layer's chunk is arriving — so the two look the same rather
 * than one of them flashing something different.
 */
function ListSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Loading files"
      className="flex min-h-0 flex-1 flex-col"
    >
      {SKELETON_ROW_WIDTHS.map((width, index) => (
        <div
          key={index}
          data-testid="file-row-skeleton"
          aria-hidden="true"
          className="flex h-9 flex-none items-center gap-2 px-3 pointer-coarse:h-11"
        >
          <span className="bg-muted size-4 shrink-0 rounded-sm" />
          <span className={cn('bg-muted h-3 rounded-sm', width)} />
          <span className="bg-muted ml-auto h-2.5 w-10 shrink-0 rounded-sm" />
        </div>
      ))}
    </div>
  );
}

/**
 * The workspace file browser: the current folder on the left, and the selected
 * file — its content or its change log, whichever `view` names — beside it.
 *
 * `path`/`file`/`view` arrive as props rather than being read here
 * directly — the route wrapper owns `Route.useSearch()`/`useNavigate()` so
 * this component stays free of router coupling, matching `WorkflowGraphPage`.
 */
export function BrowsePage({
  workspaceId,
  path,
  selectedFile,
  view,
  onNavigate,
  onSelectFile,
  onViewChange,
  onFileDeleted,
  onFileMoved,
  onCloseFile,
}: {
  workspaceId: string;
  path: string;
  selectedFile: string | undefined;
  view: FileView;
  onNavigate: (path: string) => void;
  onSelectFile: (path: string) => void;
  onViewChange: (view: FileView) => void;
  onFileDeleted: (path: string) => void;
  /**
   * A file moved, so anything holding its old path is now holding nothing.
   * Raised rather than navigated because identity here is the path: this page
   * stays router-free and the route owns every search-param write (FR-027).
   */
  onFileMoved?: (from: string, to: string) => void;
  /** Stops looking at the selected file, giving the listing its width back. */
  onCloseFile: () => void;
}) {
  const { listing, isPending, loadFailed, refetch } = useWorkspaceFileListing(
    workspaceId,
    path,
  );
  // A placeholder only once the wait is worth remarking on (spec 079 FR-017).
  const showPlaceholder = usePendingDisplay(isPending);
  const entries = listing?.entries ?? [];
  const uploads = useFileUploads(workspaceId, path, entries);
  const isPhone = usePhoneWidth();
  const canWork = useCanWork();

  const [movingEntry, setMovingEntry] = useState<FileEntry | undefined>(
    undefined,
  );
  /**
   * A move the service refused because the destination is taken, held so the
   * reader can confirm replacing it and the very same request can be retried
   * with `overwrite` (research R7). The collision is detected by the service,
   * not guessed at here: checking first would be a race.
   */
  const [conflicting, setConflicting] = useState<MoveFileRequest | undefined>(
    undefined,
  );
  const [rowDragActive, setRowDragActive] = useState(false);
  // How the width is shared once a file is open, remembered on this device.
  const previewLayout = useRememberedLayout('workspace-files-preview', [
    'files-list',
    'files-preview',
  ]);

  const { move, pendingPaths: movePending } = useMoveWorkspaceFile(
    workspaceId,
    {
      onMoved: (result) => {
        onFileMoved?.(result.source_path, result.destination_path);
      },
      onConflict: setConflicting,
      onFailed: (name, data) => {
        toast.error(getMoveFailureMessage(name, data));
      },
    },
  );

  /** The item whose rename dialog is open. */
  const [renaming, setRenaming] = useState<FileEntry | undefined>(undefined);
  /**
   * The service's refusal of the last attempted name, shown inside the dialog
   * so the reader can pick another. Cleared whenever the dialog closes; the
   * dialog itself stops showing it once the name is edited, since the message
   * was about a name no longer proposed.
   */
  const [renameProblem, setRenameProblem] = useState<string | undefined>(
    undefined,
  );
  const { rename, pendingPaths: renamePending } = useRenameWorkspaceFile(
    workspaceId,
    {
      onRenamed: (result) => {
        setRenaming(undefined);
        setRenameProblem(undefined);
        // A rename is a move to the page's other readers: the previewed file
        // follows to its new path the same way (FR-024).
        onFileMoved?.(result.source_path, result.destination_path);
      },
      onNameTaken: (name) => {
        setRenameProblem(`“${name}” already exists here.`);
      },
      onGone: (name) => {
        setRenaming(undefined);
        setRenameProblem(undefined);
        toast.error(`"${name}" is no longer there.`);
      },
      onFailed: (message) => {
        setRenaming(undefined);
        setRenameProblem(undefined);
        toast.error(message);
      },
    },
  );
  // One busy set for the listing: a row mid-move and a row mid-rename are
  // equally not to be touched.
  const pendingPaths = new Set([...movePending, ...renamePending]);

  // Held outside the listing pane: changing folder puts the listing query back
  // into its pending state, which takes the pane off screen, and the reader's
  // chosen sort went with it. Stored on the device, so a reload does not undo
  // the choice either.
  const sort = useFileSortStore((state) => state.sort);
  const setSort = useFileSortStore((state) => state.setSort);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const createFolder = useCreateWorkspaceFolder(workspaceId, path);

  // Escape is the way back to the full-width listing, alongside the preview
  // header's close control. Anything layered over the page — a dialog, a menu —
  // handles its own Escape first, and closing the file underneath it as well
  // would take two things away for one keypress.
  const fileIsOpen = selectedFile !== undefined;
  useEffect(() => {
    if (!fileIsOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('[role="dialog"],[role="menu"],[role="alertdialog"]')
      ) {
        return;
      }
      onCloseFile();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [fileIsOpen, onCloseFile]);

  // Both actions check what the folder already holds — for a name that would
  // be overwritten (FR-009), and for one already taken — so neither is offered
  // until that is known. A pending listing answers "holds nothing", which
  // would wave every collision through.
  const folderLoaded = listing !== null && listing !== undefined;

  // One file list, shown full width or beside the preview.
  const fileList = (
    <DropTarget uploads={uploads} suppressed={rowDragActive} className="flex-1">
      {/* Without the drag layer, rows stay inert (`FileDragContext`'s default):
          nothing moves for someone only reading the workspace (spec 189). */}
      {canWork ? (
        <FileDragLayerLazy
          entries={entries}
          onDragActiveChange={setRowDragActive}
          onMove={(entry, destinationPath) => {
            move({
              source_path: entry.path,
              destination_path: destinationPath,
              overwrite: false,
            });
          }}
          fallback={<ListSkeleton />}
        >
          <FileListPane
            workspaceId={workspaceId}
            currentPath={path}
            entries={entries}
            selectedPath={selectedFile}
            sort={sort}
            onSortChange={setSort}
            onNavigate={onNavigate}
            onSelectFile={onSelectFile}
            onFileDeleted={onFileDeleted}
            onRequestMove={canWork ? setMovingEntry : undefined}
            onRequestRename={canWork ? setRenaming : undefined}
            pendingMovePaths={pendingPaths}
          />
        </FileDragLayerLazy>
      ) : (
        <FileListPane
          workspaceId={workspaceId}
          currentPath={path}
          entries={entries}
          selectedPath={selectedFile}
          sort={sort}
          onSortChange={setSort}
          onNavigate={onNavigate}
          onSelectFile={onSelectFile}
          onFileDeleted={onFileDeleted}
          onRequestMove={canWork ? setMovingEntry : undefined}
          onRequestRename={canWork ? setRenaming : undefined}
          pendingMovePaths={pendingPaths}
        />
      )}
    </DropTarget>
  );

  return (
    <PagePlaceholder
      // The tree and the preview scroll on their own.
      fillsWindow
      surface="inverse"
      eyebrow="Workspace"
      title="Files"
      description="The shared file space for this workspace: uploaded inputs, data carried between tasks, and outputs agents chose to publish."
      icon={FilesIcon}
      actions={
        folderLoaded && canWork ? (
          // No box from `md` up; on a phone the two share the band's width,
          // Upload last (spec 272).
          <div className="contents max-md:flex max-md:w-full max-md:gap-2 max-md:[&>*]:flex-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setNewFolderOpen(true);
              }}
            >
              <FolderPlusIcon aria-hidden="true" />
              New folder
            </Button>
            <UploadMenu
              onPickFiles={uploads.pickFiles}
              onPickFolder={uploads.pickFolder}
            />
          </div>
        ) : undefined
      }
    >
      {isPending ? (
        showPlaceholder && (
          // Rows-shaped placeholders rather than one swapped region: the arriving
          // listing lands where the eye already is, instead of the page jumping
          // under the reader. Same reasoning as the workflow list.
          <PendingRegion
            label="Loading files"
            className="border-border min-h-0 flex-1 rounded-lg border"
          >
            {SKELETON_ROW_WIDTHS.map((width, index) => (
              <div
                key={index}
                data-testid="file-row-skeleton"
                aria-hidden="true"
                className="flex h-9 flex-none items-center gap-2 px-3 pointer-coarse:h-11"
              >
                <Skeleton className="size-4 shrink-0 rounded-sm" />
                <Skeleton className={cn('h-3 rounded-sm', width)} />
                <Skeleton className="ml-auto h-2.5 w-10 shrink-0 rounded-sm" />
              </div>
            ))}
          </PendingRegion>
        )
      ) : loadFailed ? (
        <EmptyState
          icon={FolderOpenIcon}
          description="This folder could not be loaded."
          action={<RetryButton onRetry={() => void refetch()} />}
        />
      ) : listing === null || listing === undefined ? (
        <NotFoundState
          title="Folder not found"
          description="This folder does not exist in this workspace. It may have been renamed, moved, or deleted since the link was shared."
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <Breadcrumb path={listing.path} onNavigate={onNavigate} />
          {entries.length === 0 ? (
            // An empty folder is still a drop target, and still says what it is
            // for: this is the one place where "upload the first file" is the
            // only thing anybody came here to do.
            <DropTarget uploads={uploads} className="flex-1">
              <EmptyState
                mark={<FilesMark />}
                description={
                  canWork
                    ? 'This folder is empty. Upload files or a folder here, or drop them anywhere in this panel.'
                    : 'This folder is empty.'
                }
                action={
                  canWork ? (
                    <UploadMenu
                      onPickFiles={uploads.pickFiles}
                      onPickFolder={uploads.pickFolder}
                    />
                  ) : undefined
                }
              />
            </DropTarget>
          ) : (
            /* Master-detail: the listing owns the whole width until there is
               something to detail. Preview and history used to be mounted
               always, so two "Select a file…" placeholders held about half the
               screen while the pane that actually had content was capped at
               384px — and a filename had roughly 100px of that to render in. */
            <div className="border-border flex min-h-0 flex-1 rounded-lg border">
              {selectedFile === undefined ? (
                fileList
              ) : isPhone ? (
                /* A phone has no width to share (spec 173): the open file
                   takes the card, and closing it returns to the listing. */
                <div
                  data-testid="preview-pane"
                  className="flex min-h-0 min-w-0 flex-1 flex-col"
                >
                  <PreviewPane
                    workspaceId={workspaceId}
                    entries={entries}
                    selectedFile={selectedFile}
                    view={view}
                    onViewChange={onViewChange}
                    onClose={onCloseFile}
                  />
                </div>
              ) : (
                /* With a file open, the reader decides how the width is shared
                   between the listing and the preview. The split is remembered on
                   this device; double-clicking the divider restores 28rem. */
                <ResizablePanelGroup {...previewLayout}>
                  <ResizablePanel
                    id="files-list"
                    defaultSize="28rem"
                    minSize="16rem"
                    maxSize="70%"
                    className="flex min-h-0"
                  >
                    {fileList}
                  </ResizablePanel>
                  <ResizableHandle aria-label="Resize the file list and preview" />
                  <ResizablePanel
                    id="files-preview"
                    minSize="20rem"
                    className="flex min-h-0"
                  >
                    <div
                      data-testid="preview-pane"
                      className="flex min-h-0 min-w-0 flex-1 flex-col"
                    >
                      <PreviewPane
                        workspaceId={workspaceId}
                        entries={entries}
                        selectedFile={selectedFile}
                        view={view}
                        onViewChange={onViewChange}
                        onClose={onCloseFile}
                      />
                    </div>
                  </ResizablePanel>
                </ResizablePanelGroup>
              )}
            </div>
          )}

          <UploadTray
            isCollecting={uploads.isCollecting}
            items={uploads.items}
            onRetryFailed={uploads.retryFailed}
            onDismiss={uploads.dismissUploads}
          />
        </div>
      )}

      <MoveToDialog
        open={movingEntry !== undefined}
        workspaceId={workspaceId}
        moving={movingEntry}
        onCancel={() => {
          setMovingEntry(undefined);
        }}
        onMove={(destinationPath) => {
          const entry = movingEntry;
          setMovingEntry(undefined);
          if (entry === undefined) return;
          move({
            source_path: entry.path,
            destination_path: destinationPath,
            overwrite: false,
          });
        }}
      />

      <OverwriteConfirmDialog
        open={conflicting !== undefined}
        fileName={conflicting?.destination_path.split('/').pop() ?? ''}
        onCancel={() => {
          setConflicting(undefined);
        }}
        onConfirm={() => {
          const request = conflicting;
          setConflicting(undefined);
          if (request === undefined) return;
          // The same request the service already refused, this time saying so
          // explicitly — the reader has now been shown what is at stake.
          move({ ...request, overwrite: true });
        }}
      />

      <RenameDialog
        // Keyed so a different item gets fresh dialog state.
        key={renaming?.path}
        open={renaming !== undefined}
        entry={renaming}
        existingNames={entries
          .map((entry) => entry.name)
          .filter((name) => name !== renaming?.name)}
        serverProblem={renameProblem}
        onCancel={() => {
          setRenaming(undefined);
          setRenameProblem(undefined);
        }}
        onRename={(newName) => {
          const entry = renaming;
          if (entry === undefined) return;
          setRenameProblem(undefined);
          rename(entry, newName);
        }}
      />

      <NewFolderDialog
        open={newFolderOpen}
        existingNames={entries.map((entry) => entry.name)}
        onCancel={() => {
          setNewFolderOpen(false);
        }}
        onCreate={(name) => {
          setNewFolderOpen(false);
          // Straight into it: the folder was made to put something in.
          createFolder.mutate(name, {
            onSuccess: (folderPath) => {
              onNavigate(folderPath);
            },
            onError: (error: Error) => {
              toast.error(error.message);
            },
          });
        }}
      />

      <UploadPickers
        setFilePicker={uploads.setFilePicker}
        setFolderPicker={uploads.setFolderPicker}
        onPickerChange={uploads.onPickerChange}
        collidingFileName={uploads.activeCollision?.file.name}
        onConfirmOverwrite={uploads.confirmOverwrite}
        onCancelOverwrite={uploads.cancelOverwrite}
      />
    </PagePlaceholder>
  );
}

/**
 * The file-list pane is the drop target, not the whole page (research.md R6):
 * what is dropped lands in the folder being listed, so the highlight has to
 * name that folder's region rather than the window.
 */
function DropTarget({
  uploads,
  suppressed = false,
  className,
  children,
}: {
  uploads: FileUploads;
  /**
   * A row is being dragged inside the listing, so the upload prompt stays away.
   *
   * The two never fire for each other — dnd-kit is pointer-based and never
   * touches `dataTransfer`, so these native handlers are not reached by a row
   * drag — but both would otherwise try to explain themselves at once, and
   * "drop files to upload" is the wrong thing to say to someone moving a file
   * between folders (FR-033).
   */
  suppressed?: boolean;
  className?: string;
  children: ReactNode;
}) {
  // Someone only reading the workspace drops nothing into it (spec 189).
  const canWork = useCanWork();
  return (
    <div
      className={cn('relative flex min-h-0 flex-col', className)}
      {...(canWork ? uploads.dropTargetProps : {})}
    >
      {children}
      {uploads.isDraggingOver && !suppressed && (
        <div className="border-primary bg-primary/5 pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed">
          <p className="text-primary text-sm font-medium">
            Drop files or a folder to upload
          </p>
        </div>
      )}
    </div>
  );
}
